import React, { useCallback, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { toDollar } from "../../utils/Constants/systemSetting";
import dayjs from "dayjs";

export default function SimulationChart({
  data,
  scale,
  generalInfoParams,
  YEARS,
  ITERATIONS,
  truePercentileRuns,
  percentileRuns,
}) {
  // Old method for percentiles, calculate each on frontend
  // const percentileRuns = useMemo(() => {
  //   const finals = data.map((run, i) => ({
  //     i,
  //     val: run[run.length - 1],
  //   }));
  //   const sorted = [...finals].sort((a, b) => a.val - b.val);

  //   const percentilesNeeded = [90, 75, 50, 25, 10];

  //   if (data.length <= 5) return [];

  //   return percentilesNeeded.map((percentile) => {
  //     const idx = sorted[Math.floor(sorted.length * (percentile / 100))].i;
  //     return {
  //       percentile: `${percentile}th`,
  //       data: data[idx],
  //       iterKey: `iter_${idx}`, // the actual key in formattedData
  //       isPercentile: true,
  //     };
  //   });
  // }, [data]);

  const maxPercentileValue = useMemo(() => {
    return (
      Math.round(
        Math.max(...percentileRuns.map((item) => item.data).flat()) / 10,
      ) * 10
    );
  }, [percentileRuns]);

  // Recharts wants array of objects, one per month
  const formattedData = useMemo(() => {
    if (!data.length > 0 || !data?.[0]?.length > 0) return [];
    return data[0].map((_, monthIndex) => {
      const entry = { month: `${parseInt(monthIndex + 1)}` };

      // gray lines
      data.forEach((path, iterIndex) => {
        entry[`iter_${iterIndex}`] = path[monthIndex];
      });

      // percentile lines
      percentileRuns.forEach((p) => {
        entry[p.iterKey] = p.data[monthIndex];
      });

      return entry;
    });
  }, [data, percentileRuns]);

  const CustomLegend = useCallback(({ payload }) => {
    const items = payload.filter((entry) => entry.payload.isPercentile);
    return (
      <ul
        style={{
          display: "flex",
          gap: 16,
          listStyle: "none",
          padding: 0,
          paddingTop: 20,
          paddingLeft: "7.5%",
          justifyContent: "center",
        }}
      >
        {items.map((entry) => {
          return (
            <li
              key={entry.dataKey}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
              }}
            >
              <span
                style={{ width: 10, height: 10, background: entry.color }}
              />
              {entry.payload.percentile}
            </li>
          );
        })}
      </ul>
    );
  }, []);
  const START = dayjs(generalInfoParams?.[0]?.currentDate);
  const getIncrement = useCallback(
    (totalPoints) => {
      if (totalPoints > 52 * YEARS + 1) return "days";
      if (totalPoints > 12 * YEARS + 1) return "weeks";
      return "months";
    },
    [YEARS],
  );

  const formatTick = useCallback(
    (value, totalPoints, returnFullDate = false) => {
      const unit = getIncrement(totalPoints);
      let date;
      if (unit === "days") date = START.add(parseInt(value) - 1, "day");
      else if (unit === "weeks") date = START.add(parseInt(value) - 1, "week");
      else date = START.add(parseInt(value) - 1, "month");

      return returnFullDate ? date.format("M/D/YYYY") : date.year();
    },
    [START, getIncrement],
  );
  const unit = getIncrement(data?.[0]?.length);

  const yearTicks = useMemo(() => {
    const currentYearTicks = [];
    let lastYear = null;
    for (let i = 1; i <= data[0].length; i++) {
      const date = START.add(i - 1, unit);
      if (date.year() !== lastYear && date.month() === 0) {
        currentYearTicks.push(String(i));
        lastYear = date.year();
      }
    }
    return currentYearTicks;
  }, [START, unit, data]);

  const percentileColorMap = {
    "90th": "var(--secondary-4)",
    "75th": "var(--secondary-3)",
    "50th": "var(--secondary-2)",
    "25th": "var(--secondary-1)",
    "10th": "var(--secondary)",
  };

  const CustomTooltip = useCallback(
    ({ active, payload, label, callback }) => {
      if (!active || !payload?.length) return null;
      const items = payload.filter((entry) => entry.color !== "lightgray");
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid lightgray",
            padding: 10,
          }}
        >
          <p style={{ fontWeight: "bold", textAlign: "center" }}>
            {formatTick(label, data[0].length, true)}
          </p>
          {items.map((entry) => (
            <p key={entry.dataKey} style={{ color: "black" }}>
              <span style={{ fontWeight: "bold", color: entry.color }}>
                {
                  percentileRuns.find((item) => item.iterKey === entry.name)
                    ?.percentile
                }
                :
              </span>{" "}
              <span>{toDollar(entry.value, 2)}</span>
            </p>
          ))}
        </div>
      );
    },
    [data, percentileRuns, formatTick],
  );

  return !data.length > 0 || !data?.[0]?.length > 0 ? (
    <></>
  ) : (
    <>
      {ITERATIONS && parseInt(ITERATIONS) > 100 && (
        <div style={{ fontSize: 14, textAlign: "center", flexBasis: "100%" }}>
          {/* <p>
            For performance reasons, only 100 of the total{" "}
            {parseInt(ITERATIONS).toLocaleString()} runs are shown here.
          </p>
          <p>
            Statistical analysis was perfromed using all{" "}
            {parseInt(ITERATIONS).toLocaleString()} runs.
          </p> */}
        </div>
      )}
      <LineChart
        width={800}
        height={400}
        data={formattedData}
        margin={{ bottom: 0, left: 0 }}
      >
        <XAxis
          dataKey="month"
          label={{ value: "Year", position: "insideBottom", offset: -10 }}
          ticks={yearTicks}
          tickFormatter={(value) => formatTick(value, data[0].length)}
          // interval={Math.floor(data[0].length / 10)}
        />
        <YAxis
          scale={scale}
          domain={[
            scale === "linear" ? 0 : "min",
            scale === "linear" ? maxPercentileValue * 1.1 : "max",
          ]}
          allowDataOverflow={true}
          label={{
            value: `Stock Price ($${scale === "linear" ? "" : ", Log Scale"})`,
            angle: -90,
            position: "insideLeft",
            offset: 10,
            dy: scale === "linear" ? 25 : 70,
          }}
        />
        <Tooltip content={CustomTooltip} />
        {data.map((_, iterIndex) => (
          <Line
            key={iterIndex}
            type="monotone"
            dataKey={`iter_${iterIndex}`}
            dot={false}
            activeDot={false}
            strokeOpacity={0.4}
            strokeWidth={1}
            stroke="lightgray"
            isAnimationActive={false}
          />
        ))}
        {percentileRuns.map((percentileData) => (
          <Line
            key={percentileData.percentile}
            type="monotone"
            dataKey={`${percentileData.iterKey}`}
            dot={false}
            strokeOpacity={1}
            strokeWidth={2}
            stroke={percentileColorMap[percentileData.percentile]}
            isPercentile={true}
            percentile={percentileData.percentile}
          />
        ))}
        <Legend content={<CustomLegend />} />
      </LineChart>
    </>
  );
}
